import { error as logError } from "@/services/Log"

const requiredMethods = [
  // Branding
  'getBranding',
  // Cart
  'getCart',
  'setCartItemQuantity',
  'applyCoupon',
  // General / login flow
  'initializeCheckout',
  'getOrder',
  'getCustomer',
  'signUserIn',
  'emailOptIn',
  'reloadPage',
  'initializeOrderConfirmation',
  // Shipping
  'getAddresses',
  'updateShippingOption',
  'updateShippingAddress',
  'saveAddressToUser',
  'deleteAddress',
  // Payment
  'getPaymentMethods',
  'createPaymentMethod',
  'setPaymentMethod',
  'updateBillingAddress',
  'createPaymentToken',
  'completePayment',
  'checkoutWithPaypal',
  'deletePaymentMethod',
  // Confirmation
  'payForOrder',
  'getOrderWithPayments',
  // Navigation
  'goToCart',
  'goToConfirmation',
  'goToHome',
  // Google Analytics
  'gaCheckoutEvents',
  'setPageTitle',
  'updateCurrentPathTo',
  'gaCustomer',
  'gaTrackFields',
  'gaUserStatus',
  'getExperiment',
  // Places
  'getNonShippedStates',
  'getPlacesKey',
  'getPlacesPredictions',
  'setCustomerLocalStorage',
  // Wunderkind
  'bounceXPixelTracking',
  'getPageUrl',
  'recordAbandonedCartEmail',
  // Heap
  'heapOrderTracking',
  'heapPurchaseTracking',
  'heapCustomer',
  // Loyalty
  'displayLoyaltyProgramSignup',
  'redeemLoyaltyBalance',
  'getLoyaltyBannerInfo',
  'removeStoreCredit',
  'isCustomerInLoyaltyProgram',
  // CitizensPay
  'getCitizensPayReferenceId',
  // listrak
  'listrakEmailCapture'
]

const txMethods = [
  'updateLoyaltyBalance',
]

const TX_PREFIX = 'tx.'

const rand = (max, min = 0) => Math.floor(Math.random() * (max - min)) - min

const entropy = (length) => new Array(length).fill().map(() => rand(10)).join('')

const ALIVE_SIGNAL_NAME = 'IT_LIVES!'

const frameBinding = async ({
  frame, origin, timeout = 10000, services = {},
}) => {
  const callbacks = {}
  const eventBusHandlers = {}

  const addEventListener = (eventName, handler) => {
    eventBusHandlers[eventName] = (eventBusHandlers[eventName] || []).concat(handler)
  }
  const removeEventListener = (eventName, handler) => {
    const listeners = eventBusHandlers[eventName]
    if (listeners) {
      let spliceable = listeners.indexOf(handler)
      while (~spliceable) {
        listeners.splice(spliceable, 1)
        spliceable = listeners.indexOf(handler)
      }
    }
  }

  requiredMethods.forEach((event) => {
    if (typeof services[event] !== 'function') {
      throw new Error(`You must define all required methods, found '${event}' was not of type function.`)
    }
    addEventListener(event, async ({ body, respond }) => {
      try {
        return respond(null, await services[event](body))
      } catch (providedFunctorError) {
        logError(`event: ${event}\tprovidedFunctorError`, { providedFunctorError })
        return respond(providedFunctorError)
      }
    })
  })

  const postToFrame = async ({ body, type, id: providedId }) => {
    const id = providedId || entropy(16)
    const message = { id, body, type }
    const payload = JSON.stringify(message)
    const awaitable = new Promise((resolve, reject) => {
      const handler = async ({ body, error }) => (error ? reject(error) : resolve(body))
      callbacks[id] = handler
      frame.contentWindow.postMessage(payload, origin)
    })
    return awaitable
  }

  const sendFrame = async (type, body = null) => postToFrame({ type, body })

  const txServices = {}
  txMethods.forEach((method) => {
    txServices[method] = (...args) => sendFrame(`${TX_PREFIX}${method}`, args)
  })

  const listener = async ({ data, origin: incomingOrigin }) => {
    if (incomingOrigin !== origin || typeof data !== 'string') {
      return
    }
    try{
      const {
        type, id, error, body,
      } = JSON.parse(data)
      const handlers = eventBusHandlers[type] || []
      const respond = (error, body) => {
        postToFrame({
          type, id, error, body,
        })
      }
      if (callbacks[id]) {
        callbacks[id]({ body, error })
        delete callbacks[id]
        return
      }
      handlers.forEach((handler) => handler({
        type, id, error, body, respond,
      }))
    }catch(err){
      logError(`Checkout page failed to parse message: ${err}`)
    }

  }

  const unbind = () => {
    Object.keys(callbacks).forEach((id) => delete callbacks[id])
    Object.keys(eventBusHandlers).forEach((name) => delete eventBusHandlers[name])
    frame.removeEventListener('message', listener)
  }

  window.addEventListener('message', listener)

  const exposedApi = {
    addEventListener,
    removeEventListener,
    unbind,
    sendFrame,
    txServices,
  }

  const threeWayAck = new Promise((resolve, reject) => {
    const ackListener = async ({ respond }) => {
      removeEventListener(ALIVE_SIGNAL_NAME, ackListener)
      respond()
      let timedOut = false
      const ackTimeout = setTimeout(() => {
        timedOut = true
        reject(new Error('Ack process failed with frame (timed out).'))
      }, timeout)
      const finish = (err, success) => {
        if (!timedOut) {
          clearTimeout(ackTimeout)
          return err ? reject(err) : resolve(success)
        }
      }
      try {
        await sendFrame(ALIVE_SIGNAL_NAME)
        return finish(null, exposedApi)
      } catch (bindingError) {
        return finish(bindingError)
      }
    }
    addEventListener(ALIVE_SIGNAL_NAME, ackListener)
  })

  return await threeWayAck
}

export default frameBinding
